import { assign, createMachine } from "xstate";
import {
  fetchOrchestrators,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { verifyAddress } from "@nju/data-access-portal-wwt";
import {
  fetchClientAddress,
  IClientAddress,
} from "@nju/data-access-sales-client";

interface IContext {
  ftthOrchestrator: IFtthOrchestrator | undefined;
  mobilesOrchestrators: IMobileOrchestrator[] | undefined;
  multiOrchestrator: IMultiOrderOrchestrator | undefined;
  address?: IClientAddress;
  isAddressInFtthRange: boolean;
}

type IEvent =
  | {
      type: "SUCCESS";
      ftthOrchestrator: IFtthOrchestrator | undefined;
      mobilesOrchestrators: IMobileOrchestrator[] | undefined;
      multiOrderOrchestrator: IMultiOrderOrchestrator | undefined;
    }
  | {
      type: "FETCH_CLIENT_ADDRESS_SUCCESS";
      address: IClientAddress;
    }
  | { type: "FETCH_CLIENT_ADDRESS_FAIL" }
  | { type: "ADDRESS_VERIFIED"; isAddressInFtthRange: boolean }
  | { type: "ADDRESS_NOT_VERIFIED"; isAddressInFtthRange: boolean }
  | { type: "ERROR" };

export const placeOrderSuccessMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {
        isAddressInFtthRange: false,
      } as IContext,
      events: {} as IEvent,
    },
    initial: "fetchOrchestrator",
    states: {
      fetchOrchestrator: {
        invoke: {
          src: "fetchOrchestrator",
        },
        on: {
          SUCCESS: {
            actions: "setOrchestrator",
            target: "fetchClientAddress",
          },
          ERROR: {
            target: "final",
          },
        },
      },
      fetchClientAddress: {
        invoke: {
          src: "fetchClientAddress",
        },
        on: {
          FETCH_CLIENT_ADDRESS_SUCCESS: {
            actions: "setClientAddress",
            target: "validateAddress",
          },
          FETCH_CLIENT_ADDRESS_FAIL: {
            target: "error",
          },
        },
      },
      validateAddress: {
        invoke: {
          src: "validateAddress",
        },
        on: {
          ADDRESS_VERIFIED: {
            actions: "setFtthRange",
            target: "final",
          },
          ADDRESS_NOT_VERIFIED: {
            actions: "setFtthRange",
            target: "final",
          },
        },
      },
      error: {
        type: "final",
      },
      final: {
        type: "final",
      },
    },
  },

  {
    actions: {
      setFtthRange: assign((context, event: IEvent) => {
        if (
          event.type === "ADDRESS_VERIFIED" ||
          event.type === "ADDRESS_NOT_VERIFIED"
        ) {
          return {
            isAddressInFtthRange: event.isAddressInFtthRange,
          };
        }
        return context;
      }),
      setClientAddress: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_CLIENT_ADDRESS_SUCCESS") {
          return {
            address: event.address,
          };
        }
        return context;
      }),
      setOrchestrator: assign((context, event) => {
        if (event.type === "SUCCESS") {
          return {
            ftthOrchestrator: event.ftthOrchestrator,
            mobilesOrchestrators: event.mobilesOrchestrators ?? [],
            multiOrchestrator: event.multiOrderOrchestrator,
          };
        }
        return context;
      }),
    },
    services: {
      fetchClientAddress: () => {
        return async (callback) => {
          const result = await fetchClientAddress();

          if (result.isError) {
            return callback({ type: "FETCH_CLIENT_ADDRESS_FAIL" });
          }

          return callback({
            type: "FETCH_CLIENT_ADDRESS_SUCCESS",
            address: result.unwrap(),
          });
        };
      },
      validateAddress: ({ address }) => {
        return async (callback) => {
          if (
            !address?.[0]?.geographicalAddress?.municipality ||
            !address?.[0]?.geographicalAddress?.streetNumber ||
            !address[0]?.CBSReference?.municipalityId
          ) {
            return;
          }
          const result = await verifyAddress({
            cityName: address[0]?.geographicalAddress?.municipality,
            streetName: address[0]?.geographicalAddress?.street,
            houseNumber: address[0]?.geographicalAddress?.streetNumber,
            apartmentNumber: address[0]?.geographicalAddress?.apartmentNumber,
            cityId: address[0]?.CBSReference?.municipalityId,
            streetId: address[0]?.CBSReference?.streetId,
          });

          if (result.isError) {
            return callback({
              type: "ADDRESS_NOT_VERIFIED",
              isAddressInFtthRange: false,
            });
          }

          callback({
            type: "ADDRESS_VERIFIED",
            isAddressInFtthRange: true,
          });
        };
      },
      fetchOrchestrator: () => {
        return async (callback) => {
          try {
            const orchestrator = (
              await fetchOrchestrators(true, true)
            ).unwrap();

            callback({
              type: "SUCCESS",
              ftthOrchestrator: orchestrator.ftth,
              mobilesOrchestrators: orchestrator.mobiles,
              multiOrderOrchestrator: orchestrator.multiOrder,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
    },
  },
);
